<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useQuery, useQueryClient } from "@tanstack/vue-query";

import {
  getCompetenceVerificationQueue,
  getComplianceDashboard,
  getDownloadUrl,
  getPrerequisites,
  importCompetencyMatrix,
  importSentinel,
  removeCompetence,
  savePrerequisites,
  verifyCompetence,
  type AssignmentCompliance,
  type PendingCard,
  type PrerequisiteRule,
} from "@/api/client";
import CompetenceReconcile from "@/components/CompetenceReconcile.vue";
import PageLayout from "@/components/PageLayout.vue";
import WorkerAvatar from "@/components/WorkerAvatar.vue";

const showReconcile = ref(false);

const queryClient = useQueryClient();
const { data, isPending, isError, error } = useQuery({
  queryKey: ["compliance-dashboard"],
  queryFn: () => getComplianceDashboard(),
});

// Cards awaiting office sign-off — the "to verify" queue across the workforce.
const { data: pendingCards } = useQuery({
  queryKey: ["competence-verification-queue"],
  queryFn: () => getCompetenceVerificationQueue(),
});
const cardBusy = ref<string | null>(null);
async function refreshQueue(): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: ["competence-verification-queue"] });
  // A verified card now counts toward the gate, so the dashboard shifts too.
  await queryClient.invalidateQueries({ queryKey: ["compliance-dashboard"] });
}
async function verifyCard(c: PendingCard): Promise<void> {
  cardBusy.value = c.competence_id;
  try {
    await verifyCompetence(c.worker_id, c.competence_id);
    await refreshQueue();
  } finally {
    cardBusy.value = null;
  }
}
async function rejectCard(c: PendingCard): Promise<void> {
  if (!confirm(`Reject ${c.category} for ${c.worker_name}? This removes the card.`)) return;
  cardBusy.value = c.competence_id;
  try {
    await removeCompetence(c.worker_id, c.competence_id);
    await refreshQueue();
  } finally {
    cardBusy.value = null;
  }
}
async function openCard(c: PendingCard): Promise<void> {
  if (c.front_file_id) window.open(await getDownloadUrl(c.front_file_id), "_blank");
  if (c.back_file_id) window.open(await getDownloadUrl(c.back_file_id), "_blank");
}

// Import the PR022 Minimum-Core matrix (levelled, per-division).
const matrixBusy = ref(false);
const matrixMsg = ref<string | null>(null);
async function onMatrixUpload(e: Event): Promise<void> {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  matrixBusy.value = true;
  matrixMsg.value = null;
  try {
    const r = await importCompetencyMatrix(file);
    matrixMsg.value =
      `Imported ${r.roles_imported} roles across `
      + `${r.divisions.join(", ")} (${r.competencies} competencies).`;
    await queryClient.invalidateQueries({ queryKey: ["compliance-dashboard"] });
    await queryClient.invalidateQueries({ queryKey: ["competency-roles"] });
  } catch (err) {
    matrixMsg.value = err instanceof Error ? err.message : String(err);
  } finally {
    matrixBusy.value = false;
    input.value = "";
  }
}

// Sync external on-track duties from the Sentinel CSV export.
const sentinelBusy = ref(false);
const sentinelMsg = ref<string | null>(null);
async function onSentinelUpload(e: Event): Promise<void> {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  sentinelBusy.value = true;
  sentinelMsg.value = null;
  try {
    const r = await importSentinel(file);
    const unmatched = r.unmatched_sentinel_numbers
      ? ` ${r.unmatched_sentinel_numbers} of ${r.people_in_file} not matched to a worker.`
      : "";
    sentinelMsg.value =
      `Synced ${r.competences_synced} duties to ${r.workers_synced} workers.${unmatched}`;
    await queryClient.invalidateQueries({ queryKey: ["compliance-dashboard"] });
  } catch (err) {
    sentinelMsg.value = err instanceof Error ? err.message : String(err);
  } finally {
    sentinelBusy.value = false;
    input.value = "";
  }
}

const STATS: Array<{ key: keyof NonNullable<typeof data.value>["stats"]; label: string; tone?: string }> = [
  { key: "active_workers", label: "Active workers" },
  { key: "cards_valid", label: "Cards valid", tone: "ok" },
  { key: "cards_expiring", label: "Cards expiring ≤60d", tone: "warn" },
  { key: "cards_expired", label: "Cards expired", tone: "danger" },
  { key: "medicals_valid", label: "Medicals valid", tone: "ok" },
  { key: "medicals_expired", label: "Medicals expired", tone: "danger" },
  { key: "no_medical", label: "No medical on file", tone: "warn" },
];

const toneCls: Record<string, string> = {
  ok: "text-semantic-success",
  warn: "text-semantic-warning",
  danger: "text-semantic-danger",
};

// Internal prerequisite gate (PR020) — editable list of pre-req rules.
const { data: gate } = useQuery({
  queryKey: ["prerequisites"],
  queryFn: () => getPrerequisites(),
});
const editingPrereqs = ref(false);
const draftRules = ref<PrerequisiteRule[]>([]);
const prereqBusy = ref(false);
const newRule = reactive({ competency: "", division: "", role: "", min_level: "" });

function startEditPrereqs(): void {
  draftRules.value = (gate.value?.rules ?? []).map((r) => ({ ...r }));
  editingPrereqs.value = true;
}
function addRule(): void {
  if (!newRule.competency.trim()) return;
  draftRules.value.push({
    competency: newRule.competency.trim(),
    division: newRule.division.trim() || null,
    role: newRule.role.trim() || null,
    min_level: newRule.min_level || null,
  });
  newRule.competency = newRule.division = newRule.role = newRule.min_level = "";
}
function removeRule(i: number): void {
  draftRules.value.splice(i, 1);
}
async function savePrereqs(): Promise<void> {
  prereqBusy.value = true;
  try {
    await savePrerequisites({ rules: draftRules.value, source: gate.value?.source });
    await queryClient.invalidateQueries({ queryKey: ["prerequisites"] });
    await queryClient.invalidateQueries({ queryKey: ["compliance-dashboard"] });
    editingPrereqs.value = false;
  } finally {
    prereqBusy.value = false;
  }
}
function ruleScope(r: PrerequisiteRule): string {
  const parts = [r.division, r.role].filter(Boolean);
  return parts.length ? parts.join(" · ") : "All workers";
}

// Role compliance: filter + group by project.
const assignFilter = ref<"all" | "blockers">("all");
const filteredAssignments = computed<AssignmentCompliance[]>(() => {
  const list = data.value?.assignments ?? [];
  return assignFilter.value === "blockers"
    ? list.filter((a) => a.has_role && !a.rosterable)
    : list;
});
const byProject = computed(() => {
  const groups = new Map<string, AssignmentCompliance[]>();
  for (const a of filteredAssignments.value) {
    const key = a.project_name ?? "— No project";
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(a);
  }
  return [...groups.entries()].sort((x, y) => x[0].localeCompare(y[0]));
});

function fmt(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric", month: "short", year: "numeric",
      })
    : "—";
}
</script>

<template>
  <PageLayout>
    <div class="mb-1 flex items-start justify-between gap-4">
      <h1 class="text-xl font-semibold">
        Compliance
      </h1>
      <div class="flex shrink-0 gap-2">
        <label
          class="cursor-pointer rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
          :class="{ 'pointer-events-none opacity-50': sentinelBusy }"
          title="Upload the Sentinel competence export (CSV) — syncs on-track duties to workers by Sentinel number"
        >
          {{ sentinelBusy ? "Syncing…" : "Sync Sentinel" }}
          <input
            type="file"
            accept=".csv"
            class="hidden"
            :disabled="sentinelBusy"
            @change="onSentinelUpload"
          >
        </label>
        <label
          class="cursor-pointer rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
          :class="{ 'pointer-events-none opacity-50': matrixBusy }"
          title="Upload the PR022 Minimum-Core Competency Matrix (xlsx)"
        >
          {{ matrixBusy ? "Importing…" : "Import competency matrix" }}
          <input
            type="file"
            accept=".xlsx"
            class="hidden"
            :disabled="matrixBusy"
            @change="onMatrixUpload"
          >
        </label>
      </div>
    </div>
    <p class="text-sm text-neutral-500 mb-2">
      What needs chasing across the whole workforce — cards expiring in the next
      60 days, expired medicals, missing OH records, and role-competency gaps
      (levelled against the Minimum-Core matrix).
    </p>
    <p
      v-if="sentinelMsg"
      class="mb-1 text-sm text-neutral-600"
    >
      {{ sentinelMsg }}
    </p>
    <p
      v-if="matrixMsg"
      class="mb-6 text-sm text-neutral-600"
    >
      {{ matrixMsg }}
    </p>
    <div
      v-if="!matrixMsg && !sentinelMsg"
      class="mb-6"
    />

    <!-- To verify: cards workers submitted, awaiting office sign-off -->
    <section
      v-if="pendingCards && pendingCards.length"
      class="mb-8 overflow-hidden rounded-xl border border-semantic-warning/40 bg-semantic-warning/5"
    >
      <div class="flex items-center gap-2 px-4 py-3">
        <h2 class="font-semibold">
          To verify
          <span class="text-neutral-400">({{ pendingCards.length }})</span>
        </h2>
        <p class="text-sm text-neutral-500">
          Cards submitted from the field — they don't count toward the
          rosterable gate until you sign them off.
        </p>
      </div>
      <table class="w-full text-left text-sm">
        <thead class="bg-semantic-warning/10 text-neutral-500">
          <tr>
            <th class="px-4 py-2 font-medium">
              Worker
            </th>
            <th class="px-4 py-2 font-medium">
              Card
            </th>
            <th class="px-4 py-2 font-medium">
              Expiry
            </th>
            <th class="px-4 py-2 text-right font-medium">
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="c in pendingCards"
            :key="c.competence_id"
            class="border-t border-semantic-warning/20"
          >
            <td class="px-4 py-2">
              <RouterLink
                :to="`/workers/${c.worker_id}`"
                class="font-medium text-brand-primary hover:underline"
              >
                {{ c.worker_name }}
              </RouterLink>
              <span class="text-neutral-400"> {{ c.worker_ref }}</span>
            </td>
            <td class="px-4 py-2">
              <span class="font-medium">{{ c.category }}</span>
              <span
                v-if="c.sub_category || c.issuing_body"
                class="text-neutral-500"
              >
                · {{ [c.sub_category, c.issuing_body].filter(Boolean).join(" · ") }}</span>
              <span
                v-if="c.reference"
                class="block text-xs text-neutral-400"
              >Ref {{ c.reference }}</span>
            </td>
            <td class="px-4 py-2 text-neutral-500">
              {{ c.expiry ? fmt(c.expiry) : "—" }}
            </td>
            <td class="px-4 py-2">
              <div class="flex items-center justify-end gap-3">
                <button
                  v-if="c.front_file_id"
                  type="button"
                  class="text-xs text-brand-primary hover:underline"
                  @click="openCard(c)"
                >
                  View card
                </button>
                <button
                  type="button"
                  class="rounded-md bg-brand-primary px-3 py-1.5 text-xs font-semibold text-brand-primary-on disabled:opacity-50"
                  :disabled="cardBusy === c.competence_id"
                  @click="verifyCard(c)"
                >
                  {{ cardBusy === c.competence_id ? "…" : "Verify" }}
                </button>
                <button
                  type="button"
                  class="text-xs text-neutral-400 hover:text-semantic-danger disabled:opacity-50"
                  :disabled="cardBusy === c.competence_id"
                  @click="rejectCard(c)"
                >
                  Reject
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <div
      v-if="isPending"
      class="text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="bg-semantic-danger/10 text-semantic-danger rounded-md p-4"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data"
      class="grid gap-8"
    >
      <!-- Stats -->
      <section class="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div
          v-for="s in STATS"
          :key="s.key"
          class="rounded-xl border border-neutral-200 bg-neutral-0 p-4"
        >
          <p
            class="text-2xl font-bold"
            :class="data.stats[s.key] ? (toneCls[s.tone ?? ''] ?? 'text-neutral-900') : 'text-neutral-900'"
          >
            {{ data.stats[s.key] }}
          </p>
          <p class="text-xs text-neutral-500">
            {{ s.label }}
          </p>
        </div>
        <div class="rounded-xl border border-neutral-200 bg-neutral-0 p-4">
          <p
            class="text-2xl font-bold"
            :class="data.stats.all_clear ? 'text-semantic-success' : 'text-semantic-warning'"
          >
            {{ data.stats.all_clear ? "Yes" : "No" }}
          </p>
          <p class="text-xs text-neutral-500">
            All clear
          </p>
        </div>
      </section>

      <!-- Expired cards -->
      <section>
        <h2 class="mb-2 font-semibold">
          Expired cards <span class="text-neutral-400">({{ data.expired_cards.length }})</span>
        </h2>
        <p
          v-if="!data.expired_cards.length"
          class="text-sm text-neutral-400"
        >
          No expired cards. Nice.
        </p>
        <div
          v-else
          class="overflow-hidden rounded-xl border border-neutral-200 bg-neutral-0"
        >
          <table class="w-full text-left text-sm">
            <thead class="bg-neutral-50 text-neutral-500">
              <tr>
                <th class="px-4 py-2 font-medium">
                  Worker
                </th>
                <th class="px-4 py-2 font-medium">
                  Card
                </th>
                <th class="px-4 py-2 font-medium">
                  Expiry
                </th>
                <th class="px-4 py-2 font-medium">
                  Expired
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="c in data.expired_cards"
                :key="c.worker_id + c.category"
                class="border-t border-neutral-100"
              >
                <td class="px-4 py-2">
                  <RouterLink
                    :to="`/workers/${c.worker_id}`"
                    class="font-medium text-brand-primary hover:underline"
                  >
                    {{ c.worker_name }}
                  </RouterLink>
                  <span class="text-neutral-400"> {{ c.worker_ref }}</span>
                </td>
                <td class="px-4 py-2">
                  {{ c.category }}
                </td>
                <td class="px-4 py-2 text-neutral-500">
                  {{ fmt(c.expiry) }}
                </td>
                <td class="px-4 py-2 text-semantic-danger">
                  {{ Math.abs(c.days_left ?? 0) }}d ago
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Expiring within 60 days -->
      <section>
        <h2 class="mb-2 font-semibold">
          Expiring within 60 days <span class="text-neutral-400">({{ data.expiring_cards.length }})</span>
        </h2>
        <p
          v-if="!data.expiring_cards.length"
          class="text-sm text-neutral-400"
        >
          Nothing expiring in the next 60 days.
        </p>
        <div
          v-else
          class="overflow-hidden rounded-xl border border-neutral-200 bg-neutral-0"
        >
          <table class="w-full text-left text-sm">
            <thead class="bg-neutral-50 text-neutral-500">
              <tr>
                <th class="px-4 py-2 font-medium">
                  Worker
                </th>
                <th class="px-4 py-2 font-medium">
                  Card
                </th>
                <th class="px-4 py-2 font-medium">
                  Reference
                </th>
                <th class="px-4 py-2 font-medium">
                  Expiry
                </th>
                <th class="px-4 py-2 font-medium">
                  Days
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="c in data.expiring_cards"
                :key="c.worker_id + c.category"
                class="border-t border-neutral-100"
              >
                <td class="px-4 py-2">
                  <RouterLink
                    :to="`/workers/${c.worker_id}`"
                    class="font-medium text-brand-primary hover:underline"
                  >
                    {{ c.worker_name }}
                  </RouterLink>
                  <span class="text-neutral-400"> {{ c.worker_ref }}</span>
                </td>
                <td class="px-4 py-2">
                  {{ c.category }}
                  <span
                    v-if="c.alias"
                    class="text-neutral-400"
                  >· {{ c.alias }}</span>
                </td>
                <td class="px-4 py-2 text-neutral-500">
                  {{ c.reference || "—" }}
                </td>
                <td class="px-4 py-2 text-neutral-500">
                  {{ fmt(c.expiry) }}
                </td>
                <td class="px-4 py-2 text-semantic-warning">
                  {{ c.days_left }}d left
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Internal prerequisite gate (PR020) -->
      <section>
        <div class="mb-3 flex flex-wrap items-center gap-2">
          <h2 class="font-semibold">
            Internal prerequisites
          </h2>
          <span class="text-sm text-neutral-400">
            Baseline internal requirements layered on top of the role matrix and
            Sentinel currency to decide if a worker is rosterable.
          </span>
          <button
            v-if="!editingPrereqs"
            type="button"
            class="ml-auto rounded-md border border-neutral-300 px-3 py-1 text-sm font-medium hover:bg-neutral-50"
            @click="startEditPrereqs"
          >
            Edit
          </button>
        </div>

        <!-- Read-only list -->
        <div
          v-if="!editingPrereqs"
          class="rounded-xl border border-neutral-200 bg-neutral-0 p-4"
        >
          <p
            v-if="!gate?.rules.length"
            class="text-sm text-neutral-400"
          >
            No internal prerequisites configured — nothing extra is required to
            roster. Add the client's PR020 list with “Edit”.
          </p>
          <ul
            v-else
            class="grid gap-1 text-sm"
          >
            <li
              v-for="(r, i) in gate.rules"
              :key="i"
              class="flex items-center gap-2 border-t border-neutral-50 py-1.5 first:border-0"
            >
              <span class="font-medium text-neutral-900">{{ r.competency }}</span>
              <span
                v-if="r.min_level"
                class="rounded-full bg-brand-primary/10 px-2 py-0.5 text-xs font-medium text-brand-primary"
              >≥ {{ r.min_level }}</span>
              <span class="text-xs text-neutral-400">· {{ ruleScope(r) }}</span>
            </li>
          </ul>
        </div>

        <!-- Editor -->
        <div
          v-else
          class="rounded-xl border border-neutral-200 bg-neutral-0 p-4"
        >
          <ul class="mb-3 grid gap-1 text-sm">
            <li
              v-for="(r, i) in draftRules"
              :key="i"
              class="flex items-center gap-2 border-t border-neutral-50 py-1.5 first:border-0"
            >
              <span class="font-medium text-neutral-900">{{ r.competency }}</span>
              <span
                v-if="r.min_level"
                class="rounded-full bg-brand-primary/10 px-2 py-0.5 text-xs font-medium text-brand-primary"
              >≥ {{ r.min_level }}</span>
              <span class="text-xs text-neutral-400">· {{ ruleScope(r) }}</span>
              <button
                type="button"
                class="ml-auto text-xs text-neutral-400 hover:text-semantic-danger"
                @click="removeRule(i)"
              >
                Remove
              </button>
            </li>
            <li
              v-if="!draftRules.length"
              class="py-1 text-sm text-neutral-400"
            >
              No rules yet — add one below.
            </li>
          </ul>

          <div class="flex flex-wrap items-end gap-2 border-t border-neutral-100 pt-3">
            <label class="grid gap-0.5 text-xs text-neutral-500">
              Competency
              <input
                v-model="newRule.competency"
                placeholder="e.g. Contract Induction"
                class="w-56 rounded-md border border-neutral-300 px-2 py-1 text-sm"
              >
            </label>
            <label class="grid gap-0.5 text-xs text-neutral-500">
              Division (optional)
              <input
                v-model="newRule.division"
                placeholder="e.g. ECS Power"
                class="w-36 rounded-md border border-neutral-300 px-2 py-1 text-sm"
              >
            </label>
            <label class="grid gap-0.5 text-xs text-neutral-500">
              Role (optional)
              <input
                v-model="newRule.role"
                placeholder="e.g. Dig TL"
                class="w-32 rounded-md border border-neutral-300 px-2 py-1 text-sm"
              >
            </label>
            <label class="grid gap-0.5 text-xs text-neutral-500">
              Min level
              <select
                v-model="newRule.min_level"
                class="rounded-md border border-neutral-300 px-2 py-1 text-sm"
              >
                <option value="">
                  —
                </option>
                <option value="A">A</option>
                <option value="W">W</option>
                <option value="P">P</option>
                <option value="S">S</option>
              </select>
            </label>
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
              :disabled="!newRule.competency.trim()"
              @click="addRule"
            >
              Add rule
            </button>
          </div>

          <div class="mt-3 flex gap-2">
            <button
              type="button"
              class="rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-neutral-0 hover:opacity-90"
              :disabled="prereqBusy"
              @click="savePrereqs"
            >
              {{ prereqBusy ? "Saving…" : "Save prerequisites" }}
            </button>
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
              :disabled="prereqBusy"
              @click="editingPrereqs = false"
            >
              Cancel
            </button>
          </div>
        </div>
      </section>

      <!-- Competence reconciliation (alias map) -->
      <section>
        <div class="mb-3 flex flex-wrap items-center gap-2">
          <h2 class="font-semibold">
            Competence reconciliation
          </h2>
          <span class="text-sm text-neutral-400">
            Map held competences (Sentinel / internal / manual) to the matrix &
            pre-req requirement names so the rosterable verdict matches like for
            like.
          </span>
          <button
            type="button"
            class="ml-auto rounded-md border border-neutral-300 px-3 py-1 text-sm font-medium hover:bg-neutral-50"
            @click="showReconcile = !showReconcile"
          >
            {{ showReconcile ? "Hide" : "Open reconciliation" }}
          </button>
        </div>
        <CompetenceReconcile v-if="showReconcile" />
      </section>

      <!-- Role compliance by assignment -->
      <section>
        <div class="mb-3 flex flex-wrap items-center gap-2">
          <h2 class="font-semibold">
            Role compliance by assignment
          </h2>
          <span class="text-sm text-neutral-400">
            {{ data.role_summary.total }} active assignments across projects
          </span>
        </div>
        <div class="mb-4 flex flex-wrap gap-2">
          <button
            type="button"
            class="rounded-lg border px-3 py-1.5 text-sm"
            :class="assignFilter === 'blockers'
              ? 'border-semantic-danger bg-semantic-danger/10 text-semantic-danger'
              : 'border-neutral-200 text-neutral-600'"
            @click="assignFilter = assignFilter === 'blockers' ? 'all' : 'blockers'"
          >
            Blockers · {{ data.role_summary.blockers }}
          </button>
          <span class="rounded-lg border border-neutral-200 px-3 py-1.5 text-sm text-neutral-600">
            Expiring soon · {{ data.role_summary.expiring_soon }}
          </span>
          <span class="rounded-lg border border-neutral-200 px-3 py-1.5 text-sm text-neutral-600">
            No role set · {{ data.role_summary.no_role_set }}
          </span>
          <button
            type="button"
            class="rounded-lg border px-3 py-1.5 text-sm"
            :class="assignFilter === 'all'
              ? 'border-brand-primary bg-brand-primary/10 text-brand-primary'
              : 'border-neutral-200 text-neutral-600'"
            @click="assignFilter = 'all'"
          >
            All · {{ data.role_summary.total }}
          </button>
        </div>

        <div
          v-if="!byProject.length"
          class="text-sm text-neutral-400"
        >
          No assignments to show.
        </div>
        <div
          v-for="[project, items] in byProject"
          :key="project"
          class="mb-4 rounded-xl border border-neutral-200 bg-neutral-0 p-4"
        >
          <p class="mb-2 font-medium text-neutral-900">
            {{ project }}
            <span class="text-sm font-normal text-neutral-400">· {{ items.length }} assignment(s)</span>
          </p>
          <ul class="grid gap-1">
            <li
              v-for="(a, i) in items"
              :key="i"
              class="flex items-center justify-between border-t border-neutral-50 py-1.5 first:border-0"
            >
              <span class="flex items-center gap-2 text-sm">
                <WorkerAvatar
                  :name="a.worker_name"
                  :file-id="a.image_file_id"
                  :size="28"
                />
                <span>
                  <RouterLink
                    :to="`/workers/${a.worker_id}`"
                    class="font-medium text-brand-primary hover:underline"
                  >{{ a.worker_name }}</RouterLink>
                  <span class="text-neutral-400"> {{ a.worker_ref }}</span>
                  <span class="text-neutral-500"> · {{ a.role || "No role set" }}</span>
                </span>
              </span>
              <span class="flex items-center gap-2">
                <span
                  v-if="a.has_role && a.warning_count"
                  class="rounded-full bg-semantic-warning/10 px-2 py-0.5 text-xs text-semantic-warning"
                  :title="`${a.warning_count} warning(s) — expiring soon / level not recorded`"
                >⚠ {{ a.warning_count }}</span>
                <span
                  v-if="a.has_role && !a.rosterable"
                  class="rounded-full bg-semantic-danger/10 px-2 py-0.5 text-xs font-medium text-semantic-danger"
                  :title="a.missing.join(', ')"
                >Not rosterable · {{ a.blocker_count }}</span>
                <span
                  v-else-if="a.has_role"
                  class="rounded-full bg-semantic-success/10 px-2 py-0.5 text-xs font-medium text-semantic-success"
                >Rosterable</span>
                <span
                  v-else
                  class="text-xs text-neutral-400"
                >No requirements</span>
              </span>
            </li>
          </ul>
        </div>
      </section>

      <!-- Medicals needing attention -->
      <section>
        <h2 class="mb-2 font-semibold">
          Medicals needing attention <span class="text-neutral-400">({{ data.medicals_attention.length }})</span>
        </h2>
        <p
          v-if="!data.medicals_attention.length"
          class="text-sm text-neutral-400"
        >
          Every active worker has a medical on file.
        </p>
        <div
          v-else
          class="overflow-hidden rounded-xl border border-neutral-200 bg-neutral-0"
        >
          <table class="w-full text-left text-sm">
            <thead class="bg-neutral-50 text-neutral-500">
              <tr>
                <th class="px-4 py-2 font-medium">
                  Worker
                </th>
                <th class="px-4 py-2 font-medium">
                  Role
                </th>
                <th class="px-4 py-2 font-medium">
                  Last medical
                </th>
                <th class="px-4 py-2 font-medium">
                  Reason
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="m in data.medicals_attention"
                :key="m.worker_id"
                class="border-t border-neutral-100"
              >
                <td class="px-4 py-2">
                  <RouterLink
                    :to="`/workers/${m.worker_id}`"
                    class="font-medium text-brand-primary hover:underline"
                  >
                    {{ m.worker_name }}
                  </RouterLink>
                  <span class="text-neutral-400"> {{ m.worker_ref }}</span>
                  <span
                    v-if="m.employment_type"
                    class="block text-xs text-neutral-400"
                  >{{ m.employment_type }}</span>
                </td>
                <td class="px-4 py-2 text-neutral-500">
                  {{ m.role || "—" }}
                </td>
                <td class="px-4 py-2 text-neutral-500">
                  {{ fmt(m.last_medical) }}
                </td>
                <td class="px-4 py-2 text-semantic-warning">
                  {{ m.reason }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </PageLayout>
</template>
